import { ExternalLink, Newspaper } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import type { FeedItem } from '@/types/headerFeed';

interface MobileLatestSheetProps {
  items: FeedItem[];
  children?: React.ReactNode;
}

function formatTimeAgo(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${diffDays}d ago`;
}

function getCategoryLabel(category: FeedItem['category']): { label: string; className: string } {
  switch (category) {
    case 'breaking':
      return { label: 'Breaking', className: 'bg-destructive text-destructive-foreground' };
    case 'ai':
      return { label: 'AI', className: 'bg-purple-500/20 text-purple-500' };
    case 'cyber':
      return { label: 'Cyber', className: 'bg-cyan-500/20 text-cyan-500' };
    default:
      return { label: 'News', className: 'bg-muted text-muted-foreground' };
  }
}

export function MobileLatestSheet({ items, children }: MobileLatestSheetProps) {
  return (
    <Sheet>
      <SheetTrigger asChild>
        {children || (
          <Button variant="ghost" size="sm" className="gap-2">
            <Newspaper className="h-4 w-4" />
            <span>Latest</span>
          </Button>
        )}
      </SheetTrigger>
      <SheetContent side="bottom" className="h-[70vh] rounded-t-xl">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Newspaper className="h-5 w-5" />
            Latest News
          </SheetTitle>
        </SheetHeader>
        
        <ScrollArea className="h-full mt-4 pr-4">
          <div className="space-y-3 pb-8">
            {items.slice(0, 20).map((item) => {
              const categoryInfo = getCategoryLabel(item.category);
              
              return (
                <a
                  key={item.id}
                  href={item.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={cn(
                    "block p-3 rounded-lg border border-border",
                    "hover:bg-muted/50 transition-colors",
                    "focus:outline-none focus:ring-2 focus:ring-primary"
                  )}
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-medium line-clamp-2 mb-1">
                        {item.title}
                      </h3>
                      <div className="flex items-center gap-2 text-xs">
                        <span className={cn(
                          "px-1.5 py-0.5 rounded text-[10px] font-medium uppercase",
                          categoryInfo.className
                        )}>
                          {categoryInfo.label}
                        </span>
                        <span className="text-muted-foreground">{item.source}</span>
                        <span className="text-muted-foreground">•</span>
                        <span className="text-muted-foreground">{formatTimeAgo(item.publishedAt)}</span>
                      </div>
                    </div>
                    <ExternalLink className="h-4 w-4 text-muted-foreground shrink-0 mt-1" />
                  </div>
                </a>
              );
            })}
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
